import {
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { diffWords } from "diff";
import { Button } from "@/components/ui/button";
import {
  Save,
  X,
  ChevronRight,
  ChevronLeft,
  Bot,
  User,
  ChevronDown,
  ChevronUp,
  FileText,
  Bug,
  Loader2,
  FileCode,
  Sparkles,
} from "lucide-react";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { PrettyEditor } from "./PrettyEditor";
import { AggregatedAnnotationsPanel } from "./AggregatedAnnotationsPanel";
import { AnalysisEditorTabs } from "./AnalysisEditorTabs";
import { MarkupPanelHandle } from "./MarkupPanel";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { FileAttachment, ATTACHMENT_COLORS } from "@/types/attachment";
import {
  getFileType,
  readFileAsDataUrl,
  readFileAsText,
} from "@/lib/attachmentHelpers";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface TextSelection {
  start: number;
  end: number;
  text: string;
  originalText: string;
}

interface Comment {
  id: string;
  text: string;
  color: string;
  number: number;
  textSelections: TextSelection[];
  isGeneral: boolean;
}

interface AnalysisPanelProps {
  content: string;
  onChange: (content: string) => void;
  onStateChange?: () => void;
  onDraftSynthesis?: () => void;
  isGeneratingSynthesis?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
  onSetSynthesisContent?: (content: string) => void;
}

export interface AnalysisPanelHandle {
  getState: () => {
    originalContent: string;
    currentContent: string;
    commandText: string;
    changes: any[];
    comments: Comment[];
    attachments: FileAttachment[];
  };
  setState: (state: {
    originalContent: string;
    currentContent: string;
    commandText: string;
    comments: Comment[];
    attachments: FileAttachment[];
  }) => void;
  hasUnsavedChanges: () => boolean;
  wasModifiedByAI: () => boolean;
  clearAIModifiedFlag: () => void;
}

const colorPalette = ["blue", "amber", "purple", "green", "pink", "teal"];

export const AnalysisPanel = forwardRef<
  AnalysisPanelHandle,
  AnalysisPanelProps
>(
  (
    {
      content,
      onChange,
      onStateChange,
      onDraftSynthesis,
      isGeneratingSynthesis = false,
      onCancel,
      onSave,
      onSetSynthesisContent,
    },
    ref
  ) => {
    const [showAnnotations, setShowAnnotations] = useState(true);
    const [comments, setComments] = useState<Comment[]>([]); // General analysis-level comments
    const [attachments, setAttachments] = useState<FileAttachment[]>([]);
    const [commandText, setCommandText] = useState("");
    const [selectedText, setSelectedText] = useState("");
    const [selectionRange, setSelectionRange] = useState<{
      start: number;
      end: number;
    } | null>(null);
    const [nextAnnotationNumber, setNextAnnotationNumber] = useState(1);
    const [highlightedCommentId, setHighlightedCommentId] = useState<
      string | null
    >(null);
    const [highlightedAttachmentId, setHighlightedAttachmentId] = useState<
      string | null
    >(null);
    const [isComposingComment, setIsComposingComment] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Aggregated comments from tabs
    const [currentTabComments, setCurrentTabComments] = useState<Comment[]>([]);
    const [synthesisTabComments, setSynthesisTabComments] = useState<Comment[]>([]);
    const [originalTabComments, setOriginalTabComments] = useState<Comment[]>([]);
    const [activeTab, setActiveTab] = useState<string>("current");

    // Refs to MarkupPanel instances in tabs
    const currentPanelRef = useRef<MarkupPanelHandle>(null);
    const synthesisPanelRef = useRef<MarkupPanelHandle>(null);
    const originalPanelRef = useRef<MarkupPanelHandle>(null);

    // Command panel options
    const [includeComments, setIncludeComments] = useState(true);
    const [includeChanges, setIncludeChanges] = useState(true);
    const [includeAttachments, setIncludeAttachments] = useState(true);
    const [summaryMode, setSummaryMode] = useState<string>("detailed");
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [isSynthesizing, setIsSynthesizing] = useState(false);
    const [commandPanelOpen, setCommandPanelOpen] = useState(true);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [originalContent, setOriginalContent] = useState(content);

    // New state for tabs
    const [synthesisContent, setSynthesisContent] = useState("");
    const [keyPoints, setKeyPoints] = useState<string[]>([]);
    const [contextStatements, setContextStatements] = useState<string[]>([]);

    // Track if AI just modified content (for iterate trigger)
    const aiModifiedRef = useRef(false);

    // Wrapper for onSetSynthesisContent that marks AI modifications
    const handleAISetSynthesisContent = useCallback((content: string) => {
      aiModifiedRef.current = true;
      setSynthesisContent(content);
      setHasUnsavedChanges(true);
      onStateChange?.();
      
      // Notify parent that AI modified content
      if (onSetSynthesisContent) {
        onSetSynthesisContent(content);
      }
    }, [onSetSynthesisContent, onStateChange]);

    const editorRef = useRef<HTMLDivElement>(null);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getState: () => {
        // Compute changes using diffWords
        const diff = diffWords(originalContent || "", content || "");
        const changes: any[] = [];
        let position = 0;

        diff.forEach((part, index) => {
          const contextLength = 50;
          const contextBefore = (originalContent || "").substring(
            Math.max(0, position - contextLength),
            position
          );
          const contextAfter = (originalContent || "").substring(
            position,
            Math.min((originalContent || "").length, position + contextLength)
          );

          if (part.added) {
            changes.push({
              id: `change-${Date.now()}-${index}`,
              type: "addition",
              text: part.value,
              position,
              context_before: contextBefore,
              context_after: contextAfter,
            });
          } else if (part.removed) {
            changes.push({
              id: `change-${Date.now()}-${index}`,
              type: "deletion",
              originalText: part.value,
              text: "",
              position,
              context_before: contextBefore,
              context_after: contextAfter,
            });
          }

          if (!part.removed) {
            position += part.value.length;
          }
        });

        return {
          originalContent,
          currentContent: content,
          commandText,
          changes,
          comments,
          attachments: attachments.filter((a) => !a.deleted),
        };
      },
      setState: (state) => {
        setOriginalContent(state.originalContent);
        onChange(state.currentContent);
        setCommandText(state.commandText);
        setComments(state.comments);
        setAttachments(state.attachments);

        // Calculate next annotation number from both comments and attachments
        const allNumbers = [
          ...state.comments.map((c) => c.number),
          ...state.attachments.map((a) => a.number),
        ];
        const maxNumber = allNumbers.length > 0 ? Math.max(...allNumbers) : 0;

        setNextAnnotationNumber(maxNumber + 1);
        setHasUnsavedChanges(false);
      },
      hasUnsavedChanges: () => hasUnsavedChanges,
      wasModifiedByAI: () => aiModifiedRef.current,
      clearAIModifiedFlag: () => { aiModifiedRef.current = false; },
    }));

    const handleAddComment = (commentText: string) => {
      const textSelection =
        selectedText && selectionRange
          ? {
              start: selectionRange.start,
              end: selectionRange.end,
              text: selectedText,
              originalText: selectedText,
            }
          : null;

      const assignedColor =
        colorPalette[(nextAnnotationNumber - 1) % colorPalette.length];

      const newComment: Comment = {
        id: Date.now().toString(),
        text: commentText,
        color: assignedColor,
        number: nextAnnotationNumber,
        textSelections: textSelection ? [textSelection] : [],
        isGeneral: !textSelection,
      };

      setComments([...comments, newComment]);
      setNextAnnotationNumber(nextAnnotationNumber + 1);
      setSelectedText("");
      setSelectionRange(null);
      setIsComposingComment(false);
      setHasUnsavedChanges(true);
      onStateChange?.();
    };

    const handleTextSelect = (
      text: string,
      range: { start: number; end: number }
    ) => {
      if (!isComposingComment) {
        setSelectedText(text);
        setSelectionRange(range);
      }
    };

    const handleDeleteComment = (commentId: string) => {
      setComments(comments.filter((c) => c.id !== commentId));
      setHasUnsavedChanges(true);
      onStateChange?.();
    };

    const handleEditComment = (commentId: string, newText: string) => {
      setComments(
        comments.map((c) => (c.id === commentId ? { ...c, text: newText } : c))
      );
      setHasUnsavedChanges(true);
      onStateChange?.();
    };

    const handleHighlightClick = (commentId: string) => {
      setHighlightedCommentId(commentId);
      const commentElement = document.getElementById(`comment-${commentId}`);
      if (commentElement) {
        commentElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setTimeout(() => setHighlightedCommentId(null), 2000);
    };

    // Navigate to a tab comment when clicked from aggregated view
    const handleNavigateToTabComment = (commentId: string, sourceTab: 'current' | 'synthesis' | 'original') => {
      setActiveTab(sourceTab);
      
      // Get the appropriate panel ref
      const panelRef = sourceTab === 'current' ? currentPanelRef 
                     : sourceTab === 'synthesis' ? synthesisPanelRef
                     : originalPanelRef;
      
      // Highlight the comment in that panel (after tab renders)
      setTimeout(() => {
        panelRef.current?.highlightComment(commentId);
      }, 100);
    };

    const handleSave = () => {
      // Save logic here
      toast.success("Draft saved");
      setHasUnsavedChanges(false);
    };

    const handleCancel = () => {
      if (
        hasUnsavedChanges &&
        !confirm("You have unsaved changes. Are you sure you want to cancel?")
      ) {
        return;
      }
      // Cancel logic here
    };

    // Attachment handlers
    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback(
      async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDraggingOver(false);

        const files = Array.from(e.dataTransfer.files);

        for (const file of files) {
          const fileType = getFileType(file);
          const assignedColor =
            ATTACHMENT_COLORS[
              (nextAnnotationNumber - 1) % ATTACHMENT_COLORS.length
            ];

          let dataUrl: string | undefined;
          let textContent: string | undefined;

          if (fileType === "image") {
            dataUrl = await readFileAsDataUrl(file);
          } else if (fileType === "text") {
            textContent = await readFileAsText(file);
          }

          // Check if there's selected text to link to
          const textSelection =
            selectedText && selectionRange
              ? {
                  start: selectionRange.start,
                  end: selectionRange.end,
                  text: selectedText,
                  originalText: selectedText,
                }
              : null;

          const newAttachment: FileAttachment = {
            id: Date.now().toString() + Math.random(),
            messageId: "",
            fileName: file.name,
            fileType,
            fileSize: file.size,
            storagePath: "",
            dataUrl,
            textContent,
            color: assignedColor,
            number: nextAnnotationNumber,
            textSelections: textSelection ? [textSelection] : [],
            createdAt: new Date().toISOString(),
          };

          setAttachments((prev) => [...prev, newAttachment]);
          setNextAnnotationNumber((prev) => prev + 1);
        }

        // Clear selection after adding attachments
        if (selectedText) {
          setSelectedText("");
          setSelectionRange(null);
          setIsComposingComment(false);
        }

        setHasUnsavedChanges(true);
        onStateChange?.();
      },
      [nextAnnotationNumber, selectedText, selectionRange, onStateChange]
    );

    const handleDeleteAttachment = useCallback(
      (id: string) => {
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, deleted: true } : a))
        );
        setHasUnsavedChanges(true);
        onStateChange?.();
      },
      [onStateChange]
    );

    const handleAddAttachment = useCallback(
      async (files: File[]) => {
        for (const file of files) {
          const fileType = getFileType(file);
          const assignedColor =
            ATTACHMENT_COLORS[
              (nextAnnotationNumber - 1) % ATTACHMENT_COLORS.length
            ];

          let dataUrl: string | undefined;
          let textContent: string | undefined;

          if (fileType === "image") {
            dataUrl = await readFileAsDataUrl(file);
          } else if (fileType === "text") {
            textContent = await readFileAsText(file);
          }

          // Check if there's selected text to link to
          const textSelection =
            selectedText && selectionRange
              ? {
                  start: selectionRange.start,
                  end: selectionRange.end,
                  text: selectedText,
                  originalText: selectedText,
                }
              : null;

          const newAttachment: FileAttachment = {
            id: Date.now().toString() + Math.random(),
            messageId: "",
            fileName: file.name,
            fileType,
            fileSize: file.size,
            storagePath: "",
            dataUrl,
            textContent,
            color: assignedColor,
            number: nextAnnotationNumber,
            textSelections: textSelection ? [textSelection] : [],
            createdAt: new Date().toISOString(),
          };

          setAttachments((prev) => [...prev, newAttachment]);
          setNextAnnotationNumber((prev) => prev + 1);
        }

        // Clear selection after adding attachments
        if (selectedText) {
          setSelectedText("");
          setSelectionRange(null);
          setIsComposingComment(false);
        }

        setHasUnsavedChanges(true);
        onStateChange?.();
      },
      [nextAnnotationNumber, selectedText, selectionRange, onStateChange]
    );

    const handleHighlightAttachment = useCallback((id: string) => {
      setHighlightedAttachmentId(id);
      setTimeout(() => setHighlightedAttachmentId(null), 2000);
    }, []);

    const handleRawDump = useCallback(() => {
      const debugData = {
        timestamp: new Date().toISOString(),
        originalContent: content,
        currentContent: editorRef.current?.getHTML() || "",
        commandText,
        comments: comments.filter((c) => !c.isGeneral),
        generalComments: comments.filter((c) => c.isGeneral),
        attachments: attachments.filter((a) => !a.deleted),
        changes: [], // Would come from diff
        includeOptions: { includeComments, includeChanges, includeAttachments },
        summaryMode,
      };

      const formattedOutput = `# Raw Debug Data
Generated: ${new Date().toLocaleString()}

\`\`\`json
${JSON.stringify(debugData, null, 2)}
\`\`\`
`;

      if (onSetSynthesisContent) {
        onSetSynthesisContent(formattedOutput);
        toast.success("Raw data sent to synthesis panel");
      } else {
        navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
        toast.success("Debug data copied to clipboard");
      }
    }, [
      content,
      commandText,
      comments,
      attachments,
      includeComments,
      includeChanges,
      includeAttachments,
      summaryMode,
      onSetSynthesisContent,
    ]);

    const handleSummarize = useCallback(async () => {
      setIsSummarizing(true);
      try {
        const currentContent = editorRef.current?.getHTML() || "";

        const payload = {
          originalContent: content,
          latestContent: currentContent,
          changes: [], // Would come from diff
          comments: includeComments ? comments : [],
          attachments: includeAttachments
            ? attachments.filter((a) => !a.deleted)
            : [],
          commandText,
          mode: "backward", // Looking back at what was done
          summaryType: summaryMode,
        };

        const { data, error } = await supabase.functions.invoke(
          "generate-change-summary",
          {
            body: payload,
          }
        );

        if (error) throw error;

        const summaryOutput = `# Change Summary (AI Interpretation)
Generated: ${new Date().toLocaleString()}
Mode: Backward-looking (What was done)

${data.summary}

---
*Command: ${commandText || "None"}*
*Summary Type: ${summaryMode}*
`;

        if (onSetSynthesisContent) {
          onSetSynthesisContent(summaryOutput);
          toast.success("Summary sent to synthesis panel");
        } else {
          toast.success("Summary generated: " + data.summary, {
            duration: 5000,
          });
        }
        console.log("AI Summary (backward-looking):", data.summary);
      } catch (error: unknown) {
        console.error("Error generating summary:", error);
        toast.error(
          error instanceof Error ? error.message : "Failed to generate summary"
        );
      } finally {
        setIsSummarizing(false);
      }
    }, [
      content,
      commandText,
      comments,
      attachments,
      includeComments,
      includeAttachments,
      summaryMode,
      onSetSynthesisContent,
    ]);

    const handleSynthesize = useCallback(async () => {
      setIsSynthesizing(true);
      try {
        const currentContent = editorRef.current?.getHTML() || "";

        const payload = {
          originalContent: content,
          latestContent: currentContent,
          changes: [], // Would come from diff
          comments: includeComments ? comments : [],
          attachments: includeAttachments
            ? attachments.filter((a) => !a.deleted)
            : [],
          commandText,
          mode: "forward", // Looking forward at next steps
          summaryType: summaryMode,
        };

        const { data, error } = await supabase.functions.invoke(
          "generate-change-summary",
          {
            body: payload,
          }
        );

        if (error) throw error;

        const synthesisOutput = `# Synthesis (Next Steps)
Generated: ${new Date().toLocaleString()}
Mode: Forward-looking (Where to go from here)

${data.summary}

---
*Based on analysis adjustments*
*Command: ${commandText || "None"}*
*Summary Type: ${summaryMode}*
`;

        if (onSetSynthesisContent) {
          onSetSynthesisContent(synthesisOutput);
          toast.success("Synthesis sent to synthesis panel");
        } else {
          toast.success("Synthesis generated", { duration: 3000 });
        }
        console.log("AI Synthesis (forward-looking):", data.summary);

        // Call the parent's draft synthesis with the generated content
        if (onDraftSynthesis) {
          onDraftSynthesis();
        }
      } catch (error: unknown) {
        console.error("Error generating synthesis:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to generate synthesis"
        );
      } finally {
        setIsSynthesizing(false);
      }
    }, [
      content,
      commandText,
      comments,
      attachments,
      includeComments,
      includeAttachments,
      summaryMode,
      onDraftSynthesis,
      onSetSynthesisContent,
    ]);

    return (
      <ResizablePanelGroup
        direction="horizontal"
        className="h-full bg-background"
      >
        <ResizablePanel defaultSize={showAnnotations ? 70 : 100} minSize={30}>
          <div className="h-full flex flex-col p-4 space-y-3">
            {/* Simple Title */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-muted-foreground">
                Editing Your Message
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAnnotations(!showAnnotations)}
              >
                {showAnnotations ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronLeft className="w-4 h-4" />
                )}
                {showAnnotations ? "Hide" : "Show"} Annotations
              </Button>
            </div>

            {/* Editor Area with Tabs */}
            <div
              className={`flex-1 relative overflow-hidden ${
                isDraggingOver ? "ring-2 ring-primary rounded-md" : ""
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="h-full">
                <AnalysisEditorTabs
                  currentContent={content}
                  onCurrentChange={(newContent) => {
                    onChange(newContent);
                    setHasUnsavedChanges(true);
                    onStateChange?.();
                  }}
                  comments={comments}
                  originalContent={originalContent}
                  synthesisContent={synthesisContent}
                  onSynthesisChange={(newSynthesis) => {
                    setSynthesisContent(newSynthesis);
                    setHasUnsavedChanges(true);
                    onStateChange?.();
                  }}
                  onSetSynthesisContent={handleAISetSynthesisContent}
                  keyPoints={keyPoints}
                  contextStatements={contextStatements}
                  changes={(() => {
                    const diff = diffWords(originalContent || "", content || "");
                    const changes: any[] = [];
                    let position = 0;
                    diff.forEach((part, index) => {
                      if (part.added) {
                        changes.push({
                          id: `change-${Date.now()}-${index}`,
                          type: "addition",
                          text: part.value,
                          position,
                        });
                      } else if (part.removed) {
                        changes.push({
                          id: `change-${Date.now()}-${index}`,
                          type: "deletion",
                          originalText: part.value,
                          text: "",
                          position,
                        });
                      }
                      if (!part.removed) {
                        position += part.value.length;
                      }
                    });
                    return changes;
                  })()}
                  attachments={attachments}
                  onCurrentCommentsChange={setCurrentTabComments}
                  onSynthesisCommentsChange={setSynthesisTabComments}
                  onOriginalCommentsChange={setOriginalTabComments}
                  activeTab={activeTab}
                  onActiveTabChange={setActiveTab}
                  currentPanelRef={currentPanelRef}
                  synthesisPanelRef={synthesisPanelRef}
                  originalPanelRef={originalPanelRef}
                />
              </div>
            </div>

            {/* Command Area */}
            <div className="flex gap-2 items-start pt-3">
              <textarea
                value={commandText}
                onChange={(e) => {
                  setCommandText(e.target.value);
                  setHasUnsavedChanges(true);
                  onStateChange?.();
                }}
                placeholder="Iteration command instructions (optional)..."
                className="flex-1 resize-none h-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <Button
                size="sm"
                onClick={handleSynthesize}
                disabled={isSynthesizing}
                className="mt-0"
              >
                {isSynthesizing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Iterating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-1" />
                    Iterate
                  </>
                )}
              </Button>
            </div>
          </div>
        </ResizablePanel>

        {showAnnotations && (
          <>
            <ResizableHandle withHandle />
            <AggregatedAnnotationsPanel
              generalComments={comments}
              onAddGeneralComment={handleAddComment}
              onDeleteGeneralComment={handleDeleteComment}
              onEditGeneralComment={handleEditComment}
              currentTabComments={currentTabComments}
              synthesisTabComments={synthesisTabComments}
              originalTabComments={originalTabComments}
              onNavigateToComment={handleNavigateToTabComment}
              attachments={attachments}
              onAddAttachment={handleAddAttachment}
              onDeleteAttachment={handleDeleteAttachment}
              onHighlightAttachment={handleHighlightAttachment}
              highlightedAttachmentId={highlightedAttachmentId}
            />
          </>
        )}
      </ResizablePanelGroup>
    );
  }
);
